import { useEffect, useState } from "react";
import axios from "axios";
import PageShell, { PageHeader } from "@/components/PageShell";
import Seo from "@/components/Seo";
import { Download, LogOut, Users, RefreshCcw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "nema_admin_basic";

const AdminArabicWaitlistPage = () => {
  const [token, setToken] = useState(() => sessionStorage.getItem(STORAGE_KEY) || "");
  const [creds, setCreds] = useState({ user: "", pass: "" });
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [signInError, setSignInError] = useState("");

  const load = async (t = token) => {
    if (!t) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/admin/arabic-waitlist`, {
        headers: { Authorization: `Basic ${t}` },
      });
      setLeads(data.leads || []);
      setTotal(data.total || 0);
    } catch (err) {
      if (err?.response?.status === 401) {
        sessionStorage.removeItem(STORAGE_KEY);
        setToken("");
        setSignInError("Invalid username or password.");
      } else {
        setError("Could not load waitlist.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (token) load(token); }, [token]);

  const signIn = (e) => {
    e.preventDefault();
    const t = btoa(`${creds.user}:${creds.pass}`);
    setSignInError("");
    setToken(t);
    sessionStorage.setItem(STORAGE_KEY, t);
  };

  const signOut = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setToken("");
    setLeads([]);
    setTotal(0);
    setCreds({ user: "", pass: "" });
  };

  const downloadCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/arabic-waitlist.csv`, {
        headers: { Authorization: `Basic ${token}` },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "arabic-waitlist.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      setError("CSV export failed. Please try again.");
    }
  };

  if (!token) {
    return (
      <PageShell>
        <Seo title="Admin · Arabic Waitlist — Nema TV" description="" path="/admin/arabic-waitlist" noindex />
        <div className="max-w-md mx-auto pt-24 px-6">
          <div className="rounded-3xl border border-white/10 bg-[#0F0A19] p-8">
            <h1 className="font-display text-2xl font-extrabold text-white">Admin sign in</h1>
            <p className="text-white/60 text-sm mt-2">Enter your admin credentials to see the Arabic TV waitlist.</p>
            <form onSubmit={signIn} className="mt-6 flex flex-col gap-3">
              <input
                data-testid="admin-user"
                required
                placeholder="Username"
                value={creds.user}
                onChange={(e) => setCreds({ ...creds, user: e.target.value })}
                className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#22D3EE]"
              />
              <input
                data-testid="admin-pass"
                required
                type="password"
                placeholder="Password"
                value={creds.pass}
                onChange={(e) => setCreds({ ...creds, pass: e.target.value })}
                className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#22D3EE]"
              />
              {signInError && <p className="text-sm text-red-300">{signInError}</p>}
              <button
                type="submit"
                data-testid="admin-signin"
                className="rounded-full bg-gradient-to-r from-[#6B21D9] to-[#22D3EE] text-white px-6 py-3 text-sm font-semibold hover:shadow-[0_0_25px_rgba(34,211,238,0.4)] transition-shadow"
              >
                Sign in
              </button>
            </form>
          </div>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <Seo title="Admin · Arabic Waitlist — Nema TV" description="" path="/admin/arabic-waitlist" noindex />
      <PageHeader
        eyebrow="Admin"
        title="Arabic TV Waitlist"
        subtitle="Every email that signed up to be notified when the Arabic TV Package launches."
      />

      <div className="max-w-5xl mx-auto px-6 md:px-10">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-white/85">
            <Users className="w-4 h-4 text-[#22D3EE]" />
            <span data-testid="waitlist-total"><span className="font-semibold">{total}</span> leads</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => load()}
              data-testid="waitlist-refresh"
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 text-white/85 px-4 py-2 text-sm transition-colors disabled:opacity-60"
            >
              <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button
              onClick={downloadCsv}
              data-testid="waitlist-download-csv"
              disabled={total === 0}
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#6B21D9] to-[#22D3EE] text-white px-5 py-2 text-sm font-semibold hover:shadow-[0_0_25px_rgba(34,211,238,0.4)] transition-shadow disabled:opacity-40"
            >
              <Download className="w-4 h-4" /> Export CSV
            </button>
            <button
              onClick={signOut}
              data-testid="admin-signout"
              className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 text-white/70 px-4 py-2 text-sm transition-colors"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>

        {error && (
          <div className="rounded-xl border border-red-500/30 bg-red-500/10 text-red-200 px-4 py-3 text-sm mb-4">
            {error}
          </div>
        )}

        <div className="rounded-3xl border border-white/10 bg-[#0F0A19] overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-4 md:px-6 py-3 text-[10px] uppercase tracking-[0.22em] text-white/50 border-b border-white/10">
            <div className="col-span-7">Email</div>
            <div className="col-span-3">Joined</div>
            <div className="col-span-2 text-right">Status</div>
          </div>

          {loading && total === 0 ? (
            <div className="p-8 text-center text-white/50 text-sm">Loading…</div>
          ) : leads.length === 0 ? (
            <div className="p-10 text-center text-white/50 text-sm" data-testid="waitlist-empty">
              No signups yet. When someone joins the Arabic TV waitlist, they'll show up here.
            </div>
          ) : (
            <ul>
              {leads.map((l) => (
                <li
                  key={l.email}
                  data-testid={`waitlist-row-${l.email}`}
                  className="grid grid-cols-12 gap-2 px-4 md:px-6 py-3.5 text-sm text-white/85 border-b border-white/5 last:border-b-0 hover:bg-white/[0.02] transition-colors"
                >
                  <div className="col-span-7 truncate">{l.email}</div>
                  <div className="col-span-3 text-white/60">
                    {l.joined_at ? new Date(l.joined_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—"}
                  </div>
                  <div className="col-span-2 text-right">
                    {l.active ? (
                      <span className="inline-flex items-center rounded-full bg-emerald-500/15 border border-emerald-400/40 px-2.5 py-0.5 text-[10px] uppercase tracking-[0.18em] text-emerald-300">Active</span>
                    ) : (
                      <span className="inline-flex items-center rounded-full bg-white/5 border border-white/15 px-2.5 py-0.5 text-[10px] uppercase tracking-[0.18em] text-white/50">Unsub</span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </PageShell>
  );
};

export default AdminArabicWaitlistPage;
